import   React                   ,
       { useEffect               ,
         useRef                  ,
         useState                }  from "react";

import   classNames                 from 'classnames';

import   styles                     from './PanicPanel.module.scss';

import   AnimatedWaitingIcon2       from '../../widget/animations/animatedWaitingIcon2';
import   Window                     from '../../widget/windows/window';

import { panicShutdownAllBroadcastingChannels ,
         panicStatusBroadcastingChannels      ,
         PanicJobState                        ,
         PanicReport                          }  from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';

/**
 * Bouton « Panic » du menu super-admin : l'équivalent, depuis l'interface, de la
 * commande `app:broadcasting:shutdown-all`.
 *
 * Deux garde-fous, parce que l'action coupe l'antenne de tout le monde :
 *  - elle demande confirmation avant de partir ;
 *  - elle rend compte étape par étape, y compris quand MediaMTX n'a pas pu être
 *    redémarré — un serveur resté peuplé ne doit pas passer pour un succès.
 */
export const PanicPanel = () => {

  const [ showConfirmation , setShowConfirmation ] = useState(false);
  const [ isRunning        , setIsRunning        ] = useState(false);
  const [ report           , setReport           ] = useState<PanicReport | null>(null);
  const [ errorMessage     , setErrorMessage     ] = useState('');

  const [ progress          , setProgress         ] = useState<PanicJobState | null>(null);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted   = useRef(true);

  // Applique l'état renvoyé par l'API ; rend true tant que l'arrêt n'est pas terminé.
  const applyJobState = (job: PanicJobState): boolean => {

    if (job.status === 'running') {
      setIsRunning(true);
      setProgress(job);
      return true;
    }

    setIsRunning(false);
    setProgress(null);

    if (job.status === 'done')   { setReport(job.report); }
    if (job.status === 'failed') { setErrorMessage(job.error ?? 'Arrêt interrompu.'); }

    return false;
  };

  // Sonde l'avancement toutes les 2 s. Un échec de sondage isolé n'interrompt rien :
  // l'arrêt continue côté serveur, on réessaie.
  const poll = async () => {

    const result = await panicStatusBroadcastingChannels();

    if (!mounted.current) { return; }

    const stillRunning = result.code === 1 ? applyJobState(result.payload) : true;

    if (stillRunning) { pollTimer.current = setTimeout(poll, 2000); }
  };

  // À l'ouverture : reprend le suivi d'un arrêt déjà en cours (page rechargée, autre poste).
  useEffect(() => {
    mounted.current = true;

    panicStatusBroadcastingChannels().then(result => {
      if (mounted.current && result.code === 1 && result.payload.status === 'running') {
        applyJobState(result.payload);
        pollTimer.current = setTimeout(poll, 2000);
      }
    });

    return () => {
      mounted.current = false;
      if (pollTimer.current) { clearTimeout(pollTimer.current); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmation = async (confirmed: boolean) => {

    setShowConfirmation(false);

    if (!confirmed) { return; }

    setIsRunning(true);
    setReport(null);
    setErrorMessage('');

    const result = await panicShutdownAllBroadcastingChannels();

    if (result.code !== 1) {
      setIsRunning(false);
      setErrorMessage(result.comment);
      return;
    }

    applyJobState(result.payload);
    pollTimer.current = setTimeout(poll, 2000);
  };

  const mediaMtxIncomplete = report !== null && report.mediamtxState !== 'restarted';

  return (
    <div className={classNames(styles.root)}>

      <div className={classNames(styles.head)}>
        <div>
          <p className={classNames(styles.title)}>Panic — arrêt général de la diffusion</p>
          <p className={classNames(styles.subtitle)}>
            Équivalent de la commande <code>app:broadcasting:shutdown-all</code>.
          </p>
        </div>

        <button className={classNames(styles.btn, styles.btnDanger)}
                disabled={isRunning}
                onClick={() => setShowConfirmation(true)}>
          {isRunning ? 'Arrêt en cours…' : 'Tout arrêter'}
        </button>
      </div>

      <div className={classNames(styles.card, styles.cardDanger)}>
        <p className={classNames(styles.reportLine)}>
          Cette action coupe l'antenne de <strong>toutes</strong> les chaînes en cours de diffusion,
          pour tous les clients. À n'utiliser qu'en dernier recours.
        </p>
        <ol className={classNames(styles.stepList)}>
          <li>Désactivation de toutes les chaînes actives.</li>
          <li>Suppression de tous les containers ffmpeg restants.</li>
          <li>Purge des paths orphelins MediaMTX, puis redémarrage du serveur.</li>
          <li>Purge des routes orphelines HAProxy.</li>
        </ol>
        <p className={classNames(styles.reportLine, styles.subtitle)}>
          Les chaînes sont éteintes en parallèle, en arrière-plan : l'opération se poursuit même si
          la page est fermée, et son suivi reprend à la réouverture.
        </p>
      </div>

      {
        isRunning &&
        <div className={classNames(styles.waiting)}>
          <AnimatedWaitingIcon2 size={180} />
          {
            progress !== null && progress.channels.length > 0 &&
            <ul className={classNames(styles.channelList)}>
              {progress.channels.map((channel: any) => (
                <li key={channel.id}>
                  {channel.name || 'Sans nom'}
                  {channel.status === 'success' ? ' — éteinte' : channel.status === 'error' ? ' — échec' : ' — en cours…'}
                </li>
              ))}
            </ul>
          }
          {
            progress !== null && progress.phase !== '' &&
            <p className={classNames(styles.reportLine, styles.subtitle)}>
              Étape : {({ starting: 'démarrage', deactivating: 'extinction des chaînes', containers: 'suppression des containers',
                          mediamtx: 'MediaMTX', haproxy: 'HAProxy' } as Record<string, string>)[progress.phase] ?? progress.phase}
            </p>
          }
        </div>
      }

      {
        errorMessage !== '' &&
        <div className={classNames(styles.card)}>
          <p className={classNames(styles.reportTitle)}>Échec</p>
          <p className={classNames(styles.reportLine, styles.reportLineWarning)}>{errorMessage}</p>
          <p className={classNames(styles.reportLine)}>
            Rien ne garantit que l'arrêt n'a pas commencé : vérifier l'état des chaînes avant de relancer.
          </p>
        </div>
      }

      {
        report !== null &&
        <div className={classNames(styles.card)}>

          <p className={classNames(styles.reportTitle)}>Compte rendu</p>

          <p className={classNames(styles.reportLine)}>
            {report.total === 0
               ? 'Aucune chaîne active à désactiver.'
               : `${report.deactivated} chaîne(s) désactivée(s) sur ${report.total}, ${report.errors} erreur(s).`}
          </p>

          {
            report.channels.length > 0 &&
            <ul className={classNames(styles.channelList)}>
              {report.channels.map(channel => (
                <li key={channel.id}
                    className={classNames(channel.status === 'error' ? styles.reportLineWarning : undefined)}>
                  {channel.name || 'Sans nom'}
                  {channel.status === 'error' ? ` — échec : ${channel.error ?? 'erreur inconnue'}` : ' — éteinte'}
                </li>
              ))}
            </ul>
          }

          <p className={classNames(styles.reportLine)}>
            Containers ffmpeg : {report.containers.removed} supprimé(s)
            {report.containers.failed.length > 0 ? `, ${report.containers.failed.length} échec(s)` : ''}.
          </p>

          <p className={classNames(styles.reportLine, mediaMtxIncomplete ? styles.reportLineWarning : undefined)}>
            {report.mediamtx}
          </p>

          <p className={classNames(styles.reportLine)}>{report.haproxy}</p>

        </div>
      }

      {
        showConfirmation &&
        <Window
          message                 = {<>Couper l'antenne de toutes les chaînes en cours de diffusion ?</>}
          messageType             = "question"
          messageImportance       = "important"
          onClickOnValidateButton = {() => handleConfirmation(true)}
          onClickOnCancelButton   = {() => handleConfirmation(false)}
          validButtonText         = "Tout arrêter"
          cancelButtonText        = "Annuler" />
      }

    </div>
  );
};

export default PanicPanel;
